import React, { useState, useCallback, useEffect } from 'react'

import { FONT_OPTIONS, withSystemFallback, stripSystemFallback } from '../../lib/themeTokens'
import { isValidCssValue } from '../../lib/cssValue'

/**
 * Font-family picker for the theme tokens. Stores the full CSS font
 * stack (e.g. `'Inter', sans-serif`), which is what the player writes
 * straight onto `--player-font-family`.
 *
 * Not built on the shared SelectInput: that component renders a flat
 * string[] and runs `capitalizeWord()` over each entry, which would
 * mangle a font stack into `'Inter', Sans-serif` on screen. It also has
 * no way to show a label distinct from its value.
 *
 * CUSTOM STACKS
 * A stored stack that isn't one of ours has always been preserved and shown
 * as an extra option, so nothing was ever lost -- but there was no way to
 * ENTER one, which is why CVTV's font had to be set through the API. Clients
 * host their own fonts and know what their page already loads; the token
 * accepts any string, so the only thing stopping them was this control.
 *
 * The preview cannot follow them there, and the control says so. The player
 * deliberately does not fetch fonts -- it only sets `--player-font-family` and
 * expects the hosting page to have loaded the face. `theme-preview.html` loads
 * the five we ship, and nothing else, so a custom stack falls back to
 * sans-serif in the preview while rendering correctly on the client's own
 * site. An unexplained fallback would read as "the control is broken".
 *
 * @param {object} props
 * @param {string} props.value            stored font stack; '' when unset
 * @param {string} props.preferenceName
 * @param {(value: string) => void} props.storeEdits
 */

const CUSTOM = '__custom__'

/**
 * Two stacks are the same choice if they name the same families, ignoring the
 * system tail.
 *
 * Exact string matching would have been wrong the moment the tail was
 * introduced: every preference saved before it stores the short form
 * (`'Inter', sans-serif`), and comparing literally would show all of them as
 * "(custom)" -- a cosmetic regression for every client already themed.
 */
function sameChoice (a, b) {
  return stripSystemFallback(a).toLowerCase() === stripSystemFallback(b).toLowerCase()
}

function matchOption (value) {
  if (!value) return FONT_OPTIONS[0]
  return FONT_OPTIONS.find(opt => opt.value && sameChoice(opt.value, value)) || null
}

const FontSelect = props => {
  const { value, preferenceName, storeEdits } = props
  const current = typeof value === 'string' ? value : ''

  const known = matchOption(current)
  const hasCustom = Boolean(current) && !known

  // Open on a custom value so an existing one is visible and editable rather
  // than hidden behind a select that merely mentions it.
  const [custom, setCustom] = useState(hasCustom)
  const [draft, setDraft] = useState(hasCustom ? current : '')
  const [rejected, setRejected] = useState(false)

  useEffect(() => {
    if (value && !matchOption(value)) {
      setCustom(true)
      // Only the families they chose: the tail is ours to maintain, and
      // showing it in an editable field invites someone to delete it.
      setDraft(stripSystemFallback(value))
    }
  }, [value])

  const handleChange = useCallback(ev => {
    const next = ev.target.value
    if (next === CUSTOM) {
      setCustom(true)
      setDraft(current && !matchOption(current) ? stripSystemFallback(current) : '')
      // Nothing is stored yet: choosing "Custom" is opening a field, not
      // making a choice. Writing here would blank a perfectly good stack.
      return
    }
    setCustom(false)
    setRejected(false)
    storeEdits(next)
  }, [storeEdits, current])

  const handleCustomBlur = useCallback(() => {
    const typed = draft.trim()
    if (typed === '') {
      setRejected(false)
      if (current !== '') storeEdits('')
      return
    }
    // Screened before composing, so the message points at what they typed
    // rather than at a stack they never saw.
    if (!isValidCssValue('font-family', typed)) {
      setRejected(true)
      return
    }
    setRejected(false)
    // The system tail is appended, not requested. A one-name stack is a
    // cliff -- the player never loads the font, the hosting page does, and if
    // it does not the browser drops to whatever its own default is.
    const composed = withSystemFallback(typed)
    if (composed !== current) storeEdits(composed)
  }, [draft, current, storeEdits])

  const options = hasCustom && !custom
    ? FONT_OPTIONS.concat([{ value: current, label: stripSystemFallback(current) + ' (custom)' }])
    : FONT_OPTIONS

  const selected = custom ? CUSTOM : (known ? known.value : current)
  const tail = draft.trim() ? withSystemFallback(draft.trim()).slice(draft.trim().length) : ''

  return (
    <div className="theme-font">
      <select
        className="form-control theme-font-select"
        name={preferenceName}
        value={selected}
        onChange={handleChange}
      >
        {options.map(opt => (
          <option
            key={opt.value || 'default'}
            value={opt.value}
            style={opt.value ? { fontFamily: opt.value } : undefined}
          >
            {opt.label}
          </option>
        ))}
        <option value={CUSTOM}>Custom font on your site&hellip;</option>
      </select>

      {custom ? (
        <div className={'theme-font__custom' + (rejected ? ' theme-font__custom--rejected' : '')}>
          <input
            type="text"
            className="form-control"
            value={draft}
            placeholder="'Your Font', Helvetica, sans-serif"
            aria-label="Custom font stack"
            aria-invalid={rejected}
            onChange={ev => { setDraft(ev.target.value); setRejected(false) }}
            onBlur={handleCustomBlur}
          />
          {/* The appended tail, shown but not editable. It is what the player
              falls back to when the font does not load, and it carries the
              emoji families the token would otherwise displace -- neither is
              something a client should have to know to type. */}
          {tail && !rejected ? (
            <p className="theme-font__tail">
              <code>{draft.trim()}</code>
              <span className="theme-font__tail-appended">{tail}</span>
            </p>
          ) : null}
          {rejected ? (
            <p className="theme-font__note" role="alert">
              Not a font stack the player can use. It would be discarded and the
              text would keep the current font.
            </p>
          ) : (
            <p className="theme-font__note">
              Your page has to load this font itself &mdash; the player never
              fetches one. We add the fallbacks after it, so text stays
              readable if it does not load. The preview here can only show the
              fonts we ship, so it will fall back even when your site renders
              correctly.
            </p>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default FontSelect
